import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { tipRate } from 'shared/models/api';
import { trackNow } from 'shared/models/analytic';
import { H5, AccentH5 } from 'scy/text';
import {
  sansSerifLight,
  sansSerifMedium,
  secondary,
  fontSizeS,
  uppercase,
  nonresponsiveTitle
} from 'scy/text/styles.mod.scss';
import { TextButton, LoadingButton } from 'scy/buttons';
import ContinueButton from 'scy/buttons/ContinueButton';
import { DangerWarning } from 'scy/Warning';
import StarRatingInput from 'scy/StarRatingInput';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import DefaultCourierAvatar from 'images/default-courier-avatar.png';
import margin from 'scy/margin.mod.scss';
import { primaryButton, formContainer, textareaInput } from './styles.mod.scss';
import styles from './TipRateForm.mod.scss';

export default function TipRateForm({ order, courier, handleSuccess }) {
  const { first_name, avatar_url } = courier;
  const [tip, setTip] = useState(order.order_tip || null);

  return (
    <>
      <h1
        className={classnames(
          margin.top_xs,
          margin.bottom_md,
          margin.tablet_top_xl,
          nonresponsiveTitle,
          uppercase
        )}
      >
        Rate and Tip
      </h1>
      <img
        className={styles.courierAvatar}
        src={avatar_url ? avatar_url : DefaultCourierAvatar}
      />
      {first_name && (
        <H5 uppercase marginClass={classnames(margin.top_xs, margin.bottom_md)}>
          {first_name}
        </H5>
      )}
      {tip === null ? (
        <Tip order={order} courierName={first_name} handleSuccess={setTip} />
      ) : (
        <Rate
          order={order}
          courierName={first_name}
          handleSuccess={handleSuccess}
        />
      )}
    </>
  );
}

const moneyMask = createNumberMask({
  prefix: '$',
  allowDecimal: true
});

function Tip({ order, courierName, handleSuccess }) {
  useEffect(() => {
    trackNow('Courier Tip Started', { ['order ID']: order._id });
  }, [order._id]);

  const total = (order.total || 0).toFixed(2);

  const [tipPercentage, setTipPercentage] = useState(total ? 0 : null);
  const [otherAmount, setOtherAmount] = useState(null);
  const tip =
    tipPercentage === null && !!otherAmount
      ? Number(otherAmount.slice(1)).toFixed(2)
      : (total * tipPercentage).toFixed(2);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    return tipRate({ id: order._id, amount: tip })
      .then(() => {
        setLoading(false);
        trackNow('Courier Tip Completed', { ['order ID']: order._id, tip });
        handleSuccess(tip);
      })
      .catch(({ message }) => {
        setLoading(false);
        setError(message);
      });
  }

  return (
    <form onSubmit={handleSubmit} className={formContainer}>
      <div className={styles.tip}>${tip}</div>

      <div className={styles.tipButtonContainer}>
        <TextButton
          noUnderline
          type="button"
          className={classnames(styles.tipButton, {
            [styles.selectedTipButton]: tipPercentage === 0
          })}
          onClick={() => setTipPercentage(0)}
        >
          No Tip
        </TextButton>
        <TextButton
          noUnderline
          type="button"
          className={classnames(styles.tipButton, {
            [styles.selectedTipButton]: tipPercentage === 0.1
          })}
          onClick={() => setTipPercentage(0.1)}
        >
          10%
        </TextButton>
        <TextButton
          noUnderline
          type="button"
          className={classnames(styles.tipButton, {
            [styles.selectedTipButton]: tipPercentage === 0.15
          })}
          onClick={() => setTipPercentage(0.15)}
        >
          15%
        </TextButton>
        <TextButton
          noUnderline
          type="button"
          className={classnames(styles.tipButton, {
            [styles.selectedTipButton]: tipPercentage === 0.2
          })}
          onClick={() => setTipPercentage(0.2)}
        >
          20%
        </TextButton>
        <TextButton
          noUnderline
          type="button"
          className={classnames(styles.tipButton, {
            [styles.selectedTipButton]: tipPercentage === null
          })}
          onClick={() => setTipPercentage(null)}
        >
          Other
        </TextButton>
      </div>

      {tipPercentage === null && (
        <MaskedInput
          id="otherAmount"
          name="otherAmount"
          className={styles.otherAmountInput}
          autoFocus
          placeholder="Enter an amount"
          mask={moneyMask}
          value={otherAmount}
          onChange={e => {
            setOtherAmount(e.target.value);
          }}
        />
      )}

      <div
        className={classnames(
          sansSerifLight,
          secondary,
          fontSizeS,
          margin.bottom_xl,
          margin.tablet_bottom_lg
        )}
      >
        Your order total was <span className={sansSerifMedium}>${total}</span> +{' '}
        <span className={sansSerifMedium}>${tip}</span> tip.
        <br />
        100% of the tip goes to your courier.
      </div>

      <ContinueButton
        Button={LoadingButton}
        type="submit"
        className={primaryButton}
        loading={loading}
      >
        Send tip to {courierName || 'your courier'}
      </ContinueButton>

      {error && (
        <DangerWarning className={margin.top_md}>{error}</DangerWarning>
      )}
    </form>
  );
}

function Rate({ order, courierName, handleSuccess }) {
  useEffect(() => {
    trackNow('Courier Rating Started', { ['order ID']: order._id });
  }, [order._id]);

  const [rating, setRating] = useState(0);
  const [reason, setReason] = useState('');

  function handleRating(rating) {
    setRating(rating);
  }

  function handleReason(e) {
    e.preventDefault();
    setReason(e.target.value);
  }

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    return tipRate({ id: order._id, rating })
      .then(() => {
        setLoading(false);
        trackNow('Courier Rating Completed', {
          ['order ID']: order._id,
          rating,
          reason
        });
        handleSuccess();
      })
      .catch(({ message }) => {
        setLoading(false);
        setError(message);
      });
  }

  return (
    <form onSubmit={handleSubmit} className={formContainer}>
      <AccentH5 uppercase>How’d {courierName || 'your courier'} do?</AccentH5>
      <StarRatingInput selected={rating} handleRating={handleRating} />
      <textarea
        placeholder="Details about your interaction will remain confidential…"
        className={textareaInput}
        name="reason"
        value={reason || ''}
        onChange={handleReason}
      />
      <ContinueButton
        Button={LoadingButton}
        type="submit"
        className={primaryButton}
        loading={loading}
      >
        Submit
      </ContinueButton>

      {error && (
        <DangerWarning className={margin.top_md}>{error}</DangerWarning>
      )}
    </form>
  );
}
